import { useMemo, useRef } from 'react';
import { useNodes, useEdges } from 'reactflow';
import { detectLoops, getDecisionPathLoopInfo, LoopInfo } from './loopDetection';
import { Edge } from 'reactflow';

export interface UseLoopDetectionReturn {
  loopInfo: LoopInfo;
  isEdgeInLoop: (edgeId: string) => boolean;
  getEdgeLoopType: (edgeId: string) => 'decision' | 'general' | null;
  updateEdgesWithLoopInfo: () => void;
}

export const useLoopDetection = (): UseLoopDetectionReturn => {
  const nodes = useNodes();
  const edges = useEdges();
  const previousEdgesStructure = useRef<string>('');
  const cachedLoopInfo = useRef<LoopInfo | null>(null);

  const edgesStructureKey = useMemo(() => {
    return edges
      .map(e => `${e.id}-${e.source}-${e.target}-${e.sourceHandle}-${e.targetHandle}`)
      .sort()
      .join(',');
  }, [edges]);

  const loopInfo = useMemo(() => {
    if (previousEdgesStructure.current !== edgesStructureKey) {
      previousEdgesStructure.current = edgesStructureKey;
      const newLoopInfo = detectLoops(nodes, edges);
      cachedLoopInfo.current = newLoopInfo;
      return newLoopInfo;
    }

    return cachedLoopInfo.current || {
      isLoop: false,
      loopEdges: [],
    };
  }, [edgesStructureKey, nodes, edges]);

  const isEdgeInLoop = useMemo(() => {
    return (edgeId: string): boolean => {
      return loopInfo.loopEdges.includes(edgeId);
    };
  }, [loopInfo.loopEdges]);

  const getEdgeLoopType = useMemo(() => {
    return (edgeId: string): 'decision' | 'general' | null => {
      if (!isEdgeInLoop(edgeId)) return null;

      const edge = edges.find(e => e.id === edgeId);
      if (!edge) return null;

      const sourceNode = nodes.find(n => n.id === edge.source);

      if (sourceNode?.type === 'conditionNode' && edge.sourceHandle === 'outputHandle-0') {
        const decisionLoopInfo = getDecisionPathLoopInfo(sourceNode.id, nodes, edges);
        if (decisionLoopInfo.loopEdges.includes(edgeId)) {
          return 'decision';
        }
      }

      return 'general';
    };
  }, [isEdgeInLoop, edges, nodes]);

  const updateEdgesWithLoopInfo = useMemo(() => {
    return () => {
      return edges.map((edge: Edge) => ({
        ...edge,
        data: {
          ...edge.data,
          isLoop: isEdgeInLoop(edge.id),
          loopType: getEdgeLoopType(edge.id),
        },
      }));
    };
  }, [edges, isEdgeInLoop, getEdgeLoopType]);

  return {
    loopInfo,
    isEdgeInLoop,
    getEdgeLoopType,
    updateEdgesWithLoopInfo,
  };
}; 